/**
 * The trial's AI notes are gone, and it says so the moment they are (CLA-1364).
 *
 * ## Why this is not LapsedStrip, and not TrialStrip
 *
 * The trial has two axes — 30 days or 30 notes — and until this existed only
 * the days axis told anyone anything. `ExpireTrials` flips a time-expired
 * clinic to `free`, which carries `limits.writes_locked`, and `LapsedStrip`
 * renders from that moment. The notes axis has no such flip: the server
 * refuses generation the instant the 30th note is used
 * (`assert_can_generate_note/1`), while the subscription is still `trialing`
 * — so `writes_locked` stays false, no banner renders, and the first signal
 * was a recorded consult that would not generate. That is the exact failure
 * `LapsedStrip` was built to prevent, left in place on the other axis.
 *
 * Reusing either existing strip would state something false:
 *
 * - `LapsedStrip` says "you just can't add new ones". Wrong here. Writes are
 *   NOT locked — patients, appointments, letters, the store all still work.
 *   Only AI note generation is capped, and overstating it would make people
 *   stop working when they didn't have to.
 * - `TrialStrip` is a countdown, built to create urgency about a deadline
 *   ahead. This deadline has arrived; urgency is spent and the job is to be
 *   clear, not to push.
 *
 * ## Decisions carried over from LapsedStrip deliberately
 *
 * - **Not dismissible, but quiet.** Dismissible means gone by the time it
 *   matters. Loud means punishing someone all day. Muted surface, no red —
 *   this is a state, not an error.
 * - **Role-aware.** Only someone who can manage the clinic gets the plan
 *   link; never a button that will refuse you.
 * - **Web only.** iOS shows this in its read-only plan card (CLA-849); a
 *   persistent bar carrying a plan link there edges toward App Store
 *   3.1.3(f).
 *
 * ## Why the client may derive this
 *
 * `LapsedStrip` keys off the server's `writes_locked` precisely because which
 * plans lock is data and whether the lock is enforced is a separate flag — a
 * client-side slug check would be a copy of a policy living in two settings
 * rows. Nothing like that applies here: `used` and `cap` both come from the
 * server's own meter (`Claro.Subscriptions.note_usage/1`, the same numbers
 * `TrialStrip` shows), and `used >= cap` is the identical comparison
 * `assert_can_generate_note/1` makes. No policy is being re-derived.
 */
import { Link } from 'react-router'
import { FileClock } from 'lucide-react'
import { useSubscription } from '@/api/subscription-hooks'
import { useAuthStore } from '@/stores/auth'
import { regionalText } from '@/lib/regional-copy'

export function TrialNotesSpentStrip() {
  const { data: sub } = useSubscription()
  const canManage = useAuthStore((s) => s.canManageClinic)()

  // Fails quiet, like LapsedStrip: a banner asserting a state we cannot
  // confirm is worse than no banner.
  if (!sub || sub.status !== 'trialing') return null

  // Once the overnight sweep has run the clinic is `free` and LapsedStrip owns
  // the message. Belt and braces — the status check above already excludes it —
  // but two banners saying overlapping things is the failure worth guarding.
  if (sub.writes_locked) return null

  const usage = sub.note_usage
  if (typeof usage?.used !== 'number' || typeof usage?.cap !== 'number') return null
  if (usage.cap <= 0 || usage.used < usage.cap) return null

  return (
    <div className="shrink-0 bg-surface-tertiary border-b border-border-subtle">
      <div className="px-4 py-2 flex items-center gap-3 flex-wrap">
        <FileClock className="w-4 h-4 text-ink-faint shrink-0" aria-hidden />
        <p className="flex-1 min-w-[14rem] text-[13px] text-ink-secondary">
          <span className="font-medium text-ink">
            All {usage.cap} trial AI notes have been used.
          </span>{' '}
          Everything you&rsquo;ve recorded stays here, and the rest of Claro still works — only
          new AI notes are paused.
          {/* Someone who can't change the plan needs to know that, not a link
              that 403s. Same posture as LapsedStrip and UpgradePanel. */}
          {!canManage && regionalText(' Your clinic owner can choose a plan in Settings.')}
        </p>
        {canManage && (
          <Link
            to="/settings/plan"
            className="shrink-0 text-xs font-medium link"
          >
            Choose a plan
          </Link>
        )}
      </div>
    </div>
  )
}
